"use client";

import { Button } from "@ui/components/core/Button";
import Orb2 from "@ui/components/demos/ConvAIHero/Orb/Orb2";
import ArrowUp from "@ui/icons/arrow_up.svg";
import ChatBubblePointer from "@ui/icons/chat_bubble_pointer.svg";
import Mic from "@ui/icons/mic.svg";
import ToolCall from "@ui/icons/tool_call.svg";
import { cn } from "@ui/lib/utils";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { useTranslations } from "next-intl";
import { useEffect, useMemo, useRef, useState } from "react";
import { cva } from "class-variance-authority";
import type { ConversationScript } from "./useChatAnimation";

import { TypingInput } from "./TypingInput";
import { addUserMessageWithTyping, useChatAnimation } from "./useChatAnimation";
import { useSupportAgentConfig } from "@/app/_components/SupportAgent/ConfigProvider";

interface AssistantMessage {
  content: React.ReactNode;
  loadingDuration?: number;
  loadingText?: string;
  resultContent?: React.ReactNode; // Content to show after loading completes
  type: "text" | "toolCall";
}

interface ChatMessage {
  content: AssistantMessage | string;
  id: string;
  timestamp: number;
  type: "assistant" | "user";
}

const userMessageVariant = cva(
  "flex min-h-[32px] max-w-[200px] items-center px-16 py-10 md:min-h-48 md:max-w-[280px]d md:px-20 md:py-12",
  {
    variants: {
      variant: {
        default: "bg-overlay-dark text-light rounded-[16px] rounded-br-[6px] md:rounded-xl md:rounded-tr-m",
        flat: "text-default border border-overlay-dark rounded-xl",
      },
    },
  },
);

function UserMessage({ message, variant }: { message: string; variant: "default" | "flat" }) {
  return (
    <m.div
      animate={{ opacity: 1, scale: 1 }}
      className='flex items-center justify-end gap-6 md:gap-8'
      exit={{ opacity: 0, scale: 0.9 }}
      initial={{ opacity: 0, scale: 0.9 }}
      style={{ transformOrigin: "right" }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.3,
      }}
    >
      <div className={userMessageVariant({ variant })}>
        <span className='f-paragraph-04 md:f-paragraph-03'>{message}</span>
      </div>
    </m.div>
  );
}

function LoadingSpinner() {
  return (
    <m.div
      animate={{ opacity: [0.4, 1, 0.4] }}
      transition={{
        duration: 1,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    >
      <ToolCall className='h-12 w-12 md:h-16 md:w-16' />
    </m.div>
  );
}

const assistantMessageVariant = cva(
  "md:f-paragraph-03 f-paragraph-04 flex min-h-[32px] max-w-[200px] items-center  px-16 py-12 md:min-h-48 md:max-w-[280px]  md:px-20 md:py-16 rounded-[16px] md:rounded-xl",
  {
    variants: {
      variant: {
        default: "bg-inverse text-inverse ",
        flat: "bg-default text-default shadow-[0_1px_2px_0_rgba(0,0,0,0.04),0_0px_0px_1px_rgba(0,0,0,0.06),0_2px_4px_0_rgba(0,0,0,0.04)] ml-2",
      },
    },
  },
);

function AssistantMessage({ message, variant }: { message: AssistantMessage; variant: "default" | "flat" }) {
  const [isLoading, setIsLoading] = useState(message.type === "toolCall");
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    if (message.type === "toolCall") {
      const loadingDuration = message.loadingDuration || 2000;

      const timer = setTimeout(() => {
        setIsLoading(false);
        // Small delay before showing result for smooth transition
        setTimeout(() => setShowResult(true), 200);
      }, loadingDuration);

      return () => clearTimeout(timer);
    }
  }, [message.type, message.loadingDuration]);

  return (
    <m.div
      animate={{ opacity: 1, scale: 1 }}
      className='flex items-start gap-4 md:gap-6'
      exit={{ opacity: 0, scale: 0.9 }}
      initial={{ opacity: 0, scale: 0.9 }}
      style={{ transformOrigin: "left" }}
      transition={{
        type: "spring",
        stiffness: 300,
        damping: 25,
        duration: 0.3,
      }}
    >
      {variant === "default" && (
        <m.div>
          <Orb2 className='hidden md:block' size={40} />
          <Orb2 className='block md:hidden' size={32} />
        </m.div>
      )}
      <div className='flex items-start'>
        {variant === "default" && <ChatBubblePointer className='-mr-2 mt-12 flex-shrink-0' />}
        <div className={assistantMessageVariant({ variant })}>
          <AnimatePresence mode='wait'>
            {isLoading ? (
              <m.div
                animate={{ opacity: 1, scale: 1 }}
                className='flex items-start gap-6 md:gap-8'
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                key='loading'
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  duration: 0.3,
                }}
              >
                <div className='pt-2 md:pt-1'>
                  <LoadingSpinner />
                </div>
                <m.span
                  animate={{ opacity: [0.4, 1, 0.4] }}
                  className='f-paragraph-04 md:f-paragraph-03'
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                >
                  {message.loadingText || "Processing..."}
                </m.span>
              </m.div>
            ) : showResult && message.resultContent ? (
              <m.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                key='result'
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  duration: 0.4,
                }}
              >
                {message.resultContent}
              </m.div>
            ) : !isLoading ? (
              <m.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                key='content'
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 25,
                  duration: 0.4,
                }}
              >
                {message.content}
              </m.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>
    </m.div>
  );
}

function ChatBox({ messages, variant }: { messages: ChatMessage[]; variant: "default" | "flat" }) {
  return (
    <div className='scrollbar-none pointer-events-none flex h-full flex-col justify-end gap-8 overflow-hidden overflow-y-auto overflow-x-hidden pb-16 md:gap-12 md:pb-20'>
      <AnimatePresence mode='popLayout'>
        {messages.map(message => (
          <m.div
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 0, scale: 0.9 }}
            initial={{ opacity: 0, y: 10, scale: 0.9 }}
            key={message.id}
            layout
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
              layout: {
                type: "spring",
                stiffness: 300,
                damping: 25,
              },
            }}
          >
            {message.type === "user" ? (
              <UserMessage message={message.content as string} variant={variant} />
            ) : (
              <AssistantMessage message={message.content as AssistantMessage} variant={variant} />
            )}
          </m.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

function Inputs({
  currentInputValue,
  isTyping,
  isSending,
  fullMessage,
  disableSpeechButton,
}: {
  currentInputValue: string;
  disableSpeechButton: boolean;
  fullMessage?: string;
  isSending: boolean;
  isTyping: boolean;
}) {
  const t = useTranslations("ClientKeys.conversational_ai_page");
  const { startCall: startSupportAgentCall } = useSupportAgentConfig();

  return (
    <m.div
      className='flex h-48 items-center justify-between gap-4 rounded-full border-default bg-default p-6 md:h-64 md:gap-8 md:p-8'
      style={{ boxShadow: "0px 0px 0px 1px rgba(0, 0, 0, 0.10), 0px 4px 32px 0px rgba(0, 0, 0, 0.06)" }}
    >
      <TypingInput
        currentInputValue={currentInputValue}
        fullMessage={fullMessage}
        isTyping={isTyping}
        placeholder={t("demo_scripts.enter_message")}
      />
      <div className='flex gap-6 md:gap-8'>
        <m.div animate={{ scale: isSending ? 0.9 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 20 }}>
          <Button className='pointer-events-none' size='pill-responsive-lg' variant='secondary'>
            <ArrowUp className='h-[14px] w-[14px] text-light md:h-20 md:w-20' />
          </Button>
        </m.div>
        <Button
          className={disableSpeechButton ? "pointer-events-none" : ""}
          disabled={disableSpeechButton}
          onClick={disableSpeechButton ? undefined : startSupportAgentCall}
          size='pill-responsive-lg'
          variant='secondary'
        >
          <Mic className='h-[14px] w-[14px] text-light md:h-20 md:w-20' />
        </Button>
      </div>
    </m.div>
  );
}

const chatContainerVariants = cva(
  "z-1 md:rounded-48 flex h-[max(40vh,320px)] w-[50vw] min-w-[320px] max-w-[460px] flex-col rounded-[36px] p-16 backdrop-blur-md md:p-20 lg:w-[44vw] border border-default [mask:linear-gradient(to_bottom,transparent_7.5rem,white_12.5rem)]",
  {
    variants: {
      variant: {
        default: "bg-overlay-lighter  border-t-0 rounded-t-none",
        flat: "border-b-0 rounded-b-none pt-0 md:pt-0 overflow-hidden",
      },
    },
  },
);

export interface ChatAnimationProps {
  className?: string;
  disableSpeechButton?: boolean;
  scenes?: ConversationScript[] | null;
  variant?: "default" | "flat";
}

export default function ChatAnimation({
  disableSpeechButton = false,
  className,
  scenes,
  variant = "default",
}: ChatAnimationProps) {
  const [currentInputValue, setCurrentInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [fullMessage, setFullMessage] = useState<string | undefined>();
  const hasStartedRef = useRef(false);
  const timeoutRefs = useRef<NodeJS.Timeout[]>([]);
  const t = useTranslations("ClientKeys.conversational_ai_page");

  const DEMO_SCRIPTS = useMemo<ConversationScript[]>(
    () => [
      {
        id: "order-status",
        steps: [
          { type: "user", content: t("demo_scripts.order_status.user_messages.check_order") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.order_status.assistant_messages.checking_order"),
              type: "toolCall",
              loadingText: t("demo_scripts.order_status.assistant_messages.loading_checking"),
              loadingDuration: 2000,
              resultContent: t("demo_scripts.order_status.assistant_messages.order_result"),
            },
          },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.order_status.assistant_messages.ask_tracking"),
              type: "text",
            },
          },
          { type: "user", content: t("demo_scripts.order_status.user_messages.yes_tracking") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.order_status.assistant_messages.ask_tracking"),
              type: "toolCall",
              loadingText: t("demo_scripts.order_status.assistant_messages.sending_tracking"),
              loadingDuration: 2000,
              resultContent: t("demo_scripts.order_status.assistant_messages.tracking_sent"),
            },
          },
          { type: "user", content: t("demo_scripts.order_status.user_messages.thanks") },
        ],
        autoStart: true,
        loop: false,
      },
      {
        id: "appointment-booking",
        steps: [
          { type: "user", content: t("demo_scripts.appointment_booking.user_messages.book_appointment") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.appointment_booking.assistant_messages.checking_availability"),
              type: "toolCall",
              loadingText: t("demo_scripts.appointment_booking.assistant_messages.loading_availability"),
              loadingDuration: 1800,
              resultContent: t("demo_scripts.appointment_booking.assistant_messages.availability_result"),
            },
          },
          { type: "user", content: t("demo_scripts.appointment_booking.user_messages.confirm_time") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.appointment_booking.assistant_messages.scheduling"),
              type: "toolCall",
              loadingText: t("demo_scripts.appointment_booking.assistant_messages.loading_booking"),
              loadingDuration: 1800,
              resultContent: t("demo_scripts.appointment_booking.assistant_messages.booking_confirmed"),
            },
          },
          { type: "user", content: t("demo_scripts.appointment_booking.user_messages.thanks") },
        ],
        autoStart: true,
        loop: false,
      },
      {
        id: "customer-support",
        steps: [
          { type: "user", content: t("demo_scripts.customer_support.user_messages.computer_frozen") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.customer_support.assistant_messages.greeting"),
              type: "text",
            },
          },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.customer_support.assistant_messages.checking_device"),
              type: "toolCall",
              loadingText: t("demo_scripts.customer_support.assistant_messages.loading_lookup"),
              loadingDuration: 1800,
              resultContent: t("demo_scripts.customer_support.assistant_messages.solution"),
            },
          },
          { type: "user", content: t("demo_scripts.customer_support.user_messages.thanks") },
          {
            type: "assistant",
            content: {
              content: t("demo_scripts.customer_support.assistant_messages.goodbye"),
              type: "text",
            },
          },
        ],
        autoStart: true,
        loop: false,
      },
    ],
    [t],
  );

  const addTimeoutKeyToQueue = (key: NodeJS.Timeout) => {
    timeoutRefs.current.push(key);
  };

  // Custom function to handle user message with typing effect
  const addUserMessageWithTypingLocal = async (content: string) => {
    return addUserMessageWithTyping({
      addTimeoutKeyToQueue,
      addMessage,
      setCurrentInputValue,
      setFullMessage,
      setIsSending,
      setIsTyping,
    })(content);
  };

  const { messages, addMessage, cleanup, startAutoRotation } = useChatAnimation(
    [],
    scenes || DEMO_SCRIPTS,
    variant === "default" ? addUserMessageWithTypingLocal : undefined,
  );

  useEffect(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startAutoRotation();
    }
  }, [startAutoRotation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      hasStartedRef.current = false;
      cleanup();
      timeoutRefs.current.forEach(key => clearTimeout(key));
      timeoutRefs.current = [];
    };
  }, [cleanup]);

  const visibleScenes = scenes || DEMO_SCRIPTS;

  return (
    <div className={cn(chatContainerVariants({ variant }), className)}>
      {visibleScenes && visibleScenes.length > 0 ? (
        <>
          <ChatBox messages={messages} variant={variant} />
          {variant === "default" && (
            <Inputs
              currentInputValue={currentInputValue}
              disableSpeechButton={disableSpeechButton}
              fullMessage={fullMessage}
              isSending={isSending}
              isTyping={isTyping}
            />
          )}
        </>
      ) : null}
    </div>
  );
}

// Export utility functions and types for external use
export { useChatAnimation };
export type { AssistantMessage, ChatMessage };
